import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import PeopleIcon from "@mui/icons-material/PeopleOutline";
import GroupsIcon from "@mui/icons-material/Groups2Outlined";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBookOutlined";
import GridOnIcon from "@mui/icons-material/GridOnOutlined";
import HowToRegIcon from "@mui/icons-material/HowToRegOutlined";
import SchoolIcon from "@mui/icons-material/SchoolOutlined";
import FunnelIcon from "@mui/icons-material/FilterAltOutlined";
import PaymentsIcon from "@mui/icons-material/PaymentsOutlined";
import PointOfSaleIcon from "@mui/icons-material/PointOfSaleOutlined";
import NotificationsIcon from "@mui/icons-material/NotificationsNoneOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import PersonIcon from "@mui/icons-material/PersonOutline";
import ShieldIcon from "@mui/icons-material/ShieldOutlined";
import type SvgIcon from "@mui/material/SvgIcon";
import type { PermissionKey } from "@/types/permissions";
import type { Role } from "@/types/enums";

export interface NavItem {
  label: string;
  path: string;
  icon: typeof SvgIcon;
  anyOf: PermissionKey[];
  /** Раздел бокового меню, как в прототипе. */
  group: NavGroup;
  onlyRoles?: Role[];
  excludeRoles?: Role[];
  /** Скрыть пункт при наличии указанного права: «Касса» не нужна тем, у кого есть «Финансы». */
  hideIfPermission?: PermissionKey;
}

export type NavGroup = "work" | "money" | "cabinet";

export const NAV_GROUP_LABELS: Record<NavGroup, string> = {
  work: "Работа",
  money: "Деньги и связь",
  cabinet: "Мой кабинет",
};

export const NAV_GROUP_ORDER: NavGroup[] = ["work", "money", "cabinet"];

export const NAV_ITEMS: NavItem[] = [
  { label: "Дашборд", path: "/", icon: DashboardIcon, anyOf: ["dash.view"], excludeRoles: ["STUDENT"], group: "work" },
  { label: "Регистрация", path: "/registration", icon: HowToRegIcon, anyOf: ["leads.edit"], group: "work" },
  { label: "Ученики", path: "/students", icon: PeopleIcon, anyOf: ["students.view"], group: "work" },
  { label: "Посещаемость", path: "/attendance", icon: GridOnIcon, anyOf: ["attendance.mark"], group: "work" },
  { label: "Группы", path: "/groups", icon: GroupsIcon, anyOf: ["groups.view"], group: "work" },
  { label: "Расписание", path: "/schedule", icon: CalendarIcon, anyOf: ["groups.view"], group: "work" },
  {
    label: "Журнал и оценки",
    path: "/journal",
    icon: MenuBookIcon,
    anyOf: ["attendance.mark", "attendance.all"],
    group: "work",
  },
  { label: "Преподаватели", path: "/teachers", icon: SchoolIcon, anyOf: ["teachers.view"], group: "work" },
  { label: "Заявки", path: "/leads", icon: FunnelIcon, anyOf: ["leads.view"], group: "work" },
  {
    label: "Финансы",
    path: "/finance",
    icon: PaymentsIcon,
    anyOf: ["fin.payments", "fin.debts", "fin.expense", "fin.salary", "fin.report"],
    onlyRoles: ["DIRECTOR", "ACCOUNTANT"],
    group: "money",
  },
  {
    label: "Касса",
    path: "/cashbox",
    icon: PointOfSaleIcon,
    anyOf: ["fin.payments"],
    hideIfPermission: "fin.report",
    group: "money",
  },
  { label: "Уведомления", path: "/notifications", icon: NotificationsIcon, anyOf: ["notify.view"], group: "money" },
  { label: "Аудит действий", path: "/audit", icon: ShieldIcon, anyOf: ["set.audit"], group: "money" },
  {
    label: "Настройки",
    path: "/settings",
    icon: SettingsIcon,
    anyOf: ["set.catalog", "set.users", "set.roles", "set.integr"],
    group: "money",
  },
  { label: "Мой кабинет", path: "/my", icon: PersonIcon, anyOf: [], onlyRoles: ["STUDENT"], group: "cabinet" },
];

export function isNavItemVisible(item: NavItem, role: Role, permissions: PermissionKey[]): boolean {
  if (item.onlyRoles && !item.onlyRoles.includes(role)) return false;
  if (item.excludeRoles?.includes(role)) return false;
  if (item.hideIfPermission && permissions.includes(item.hideIfPermission)) return false;
  if (item.anyOf.length === 0) return true;
  return item.anyOf.some((key) => permissions.includes(key));
}

export function navAllowed(role: Role, permissions: PermissionKey[]): NavItem[] {
  return NAV_ITEMS.filter((item) => isNavItemVisible(item, role, permissions));
}

/** Пункты меню, сгруппированные по разделам, — для боковой панели. */
export function navGroups(role: Role, permissions: PermissionKey[]): Array<{ group: NavGroup; label: string; items: NavItem[] }> {
  return NAV_GROUP_ORDER.map((group) => ({
    group,
    label: NAV_GROUP_LABELS[group],
    items: navAllowed(role, permissions).filter((item) => item.group === group),
  })).filter((section) => section.items.length > 0);
}

export function defaultRouteForRole(role: Role): string {
  return role === "STUDENT" ? "/my" : role === "ACCOUNTANT" ? "/finance" : "/";
}
